import React, { useEffect, useState } from "react"

import { useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { InputElement } from "@front-shared/elements/input.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { UtilHelper } from "../../../helpers/util.helper.tsx"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { authSelector } from "../../../store/selectors/auth.selector"
import { authDemoLogin } from "../../../store/thunks/auth.thunks"
import type { IEmailData } from "../../../types/data.types.ts"

import arrowRightIcon from "@front-shared/assets/images/icons/arrow-right2.svg"
import mailIcon from "@front-shared/assets/images/icons/mail-dark.svg"
import inviteAcceptIcon from "@front-shared/assets/images/shapes/invite-accept.svg"

export const DemoEntryComponent: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()

    const authState = useAppSelector(authSelector)

    // Encourage user to keep the email they entered into the hubspot form
    const predefinedEmail =
        UtilHelper.getRawUrlSearchParam(
            GENERAL_CONFIG.urlSearchParamsKeys.email,
        ) || ""

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useForm<IEmailData | any>({
        defaultValues: {
            email: predefinedEmail,
        },
    })

    const [isEmailDisabled, setIsEmailDisabled] = useState(!!predefinedEmail)

    // If there are errors on the prepopulated email, allow the user to edit the email
    // This way we don't depend on the referrer having proper email validation
    useEffect(() => {
        if (errors.email) {
            setIsEmailDisabled(false)
        }
    }, [errors.email])

    const onSubmit = (data: IEmailData) => {
        dispatch(
            authDemoLogin({
                payload: {
                    email: data.email,
                },
            }),
        )
    }

    if (GENERAL_CONFIG.isProduction) {
        return <Navigate replace to={ROUTES_CONFIG.loginUrl} />
    }

    return (
        <div className="relative md:px-[16px]">
            <PageHelmetElement
                title="OneVerify Demo"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <div className="w-full text-center">
                <img
                    src={inviteAcceptIcon}
                    alt="Demo entry"
                    title="Demo entry"
                    className="margin-x-center sm:w-[150px]"
                />
            </div>

            <PrimaryText typography="h1" centered className="mt-[32px]">
                Explore OneVerify
            </PrimaryText>

            <PrimaryText centered typography="subtitle" className="mt-[32px]">
                Start a guided demo to explore coverage checking and see how
                OneVerify works — no account or password required, simply enter
                your email below.
            </PrimaryText>

            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate={true}
                className="mt-[44px] flex items-start md:block"
            >
                <InputElement
                    className="mr-[24px] flex-1 md:mr-[0px]"
                    name="email"
                    label="Email address"
                    defaultValue={predefinedEmail}
                    reactHookControl={control}
                    reactHookErrors={errors}
                    reactHookValidations={{
                        required: FRONT_VALIDATION_CONFIG.required,
                        pattern: FRONT_VALIDATION_CONFIG.email,
                        maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                    }}
                    inputSuffix={
                        <img
                            src={mailIcon}
                            alt="Email address"
                            title="Email address"
                            className="ml-[8px]"
                        />
                    }
                    disabled={isEmailDisabled}
                />

                <ButtonElement
                    className="md:mt-[32px] md:text-right"
                    label="Start demo"
                    type="primary"
                    htmlType="submit"
                    size="large"
                    icon={arrowRightIcon}
                    isRightIcon
                    isLoading={authState.isLoading}
                />
            </form>
        </div>
    )
}
